import React, { useState, useEffect } from "react";
import { PAGE_SIZE } from "../../../api";
import Icon from "../../../components/Icon";
import { StatusBadge, getRouterStatus } from "../../../components/Badge";
import Skeleton from "../../../components/Skeleton";
import { ErrorState } from "../../../components/State";
import DataTable from "../../../components/DataTable";
import ServerDetailModal from "../../../components/ServerDetailModal";
import Tooltip from "../../../components/Tooltip";
import SearchInput from "../../../components/SearchInput";
import FilterSelect from "../../../components/FilterSelect";
import { KpiGrid, KpiCard } from "../../../components/KpiCard";
import {
  getAssetCategory,
  formatCategoryLabel,
  formatManagementLabel,
  getAssetVendorModel,
} from "../../../utils/assetTaxonomy";
import { useAssetStore } from "../../../store/useAssetStore";
import TopologyView from "../../../components/TopologyView";
import { fetchTopology as fetchTopologyApi } from "../services/CMDBService";

const getRouterHealthScore = (r) => {
  if (getRouterStatus(r) === "inactive") return 0;
  if (
    r?.health_score === null ||
    r?.health_score === undefined ||
    r?.health_score === ""
  ) {
    return null;
  }
  const score = Number(r.health_score);
  return Number.isFinite(score) ? score : null;
};

const getRouterLatency = (r) => {
  if (!r) return null;
  const apiStatuses = [r.status, r.health_status].map((status) =>
    String(status || "").toLowerCase().trim(),
  );
  if (apiStatuses.some((status) => ["inactive", "decommissioned"].includes(status))) {
    return null;
  }

  const lat =
    r.ping_latency_ms ??
    r.latency_ms ??
    r.specs?.ping_latency_ms ??
    r.specs?.latency_ms ??
    r.specs?.live_metrics?.ping_latency_ms ??
    r.specs?.live_metrics?.rtt_ms;
  if (lat !== null && lat !== undefined && !isNaN(Number(lat))) {
    return Number(lat);
  }
  return null;
};

export default function NetworkView() {
  const routers = useAssetStore((state) => state.routers);
  const loading = useAssetStore((state) => state.loading);
  const error = useAssetStore((state) => state.error);
  const fetchAllData = useAssetStore((state) => state.fetchAllData);

  const servers = useAssetStore((state) => state.servers);
  const [activeTab, setActiveTab] = useState("appliances");
  const [selectedRouter, setSelectedRouter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [topology, setTopology] = useState(null);
  const [topologyLoading, setTopologyLoading] = useState(false);
  const [topologyError, setTopologyError] = useState(null);

  const fetchTopology = React.useCallback(async () => {
    setTopologyLoading(true);
    setTopologyError(null);
    try {
      setTopology(await fetchTopologyApi());
    } catch {
      setTopologyError("Could not load network topology.");
    } finally {
      setTopologyLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "topology" && !topology) {
      fetchTopology();
    }
  }, [activeTab, topology, fetchTopology]);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(() => {
      fetchAllData(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  useEffect(() => {
    if (selectedRouter) {
      const updated = routers.find(
        (r) =>
          r.id === selectedRouter.id || r.hostname === selectedRouter.hostname,
      );
      if (updated) setSelectedRouter(updated);
    }
  }, [routers, selectedRouter]);

  const filteredRouters = routers.filter((r) => {
    const status = getRouterStatus(r);
    const { vendor, model } = getAssetVendorModel(r);
    const matchesSearch =
      r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.hostname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.specs?.ip_address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vendor?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      model?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      !statusFilter ||
      status === statusFilter ||
      (statusFilter === "inactive" &&
        (status === "decommissioned" || status === "down"));
    return matchesSearch && matchesStatus;
  });

  const statCards = [
    {
      label: "Total Appliances",
      value: routers.length,
      color: "#6B21A8",
      bg: "#F3E8FF",
      icon: "network",
      filterValue: "",
    },
    {
      label: "Active / Healthy",
      value: routers.filter((r) => getRouterStatus(r) === "active").length,
      color: "#15803D",
      bg: "#F0FDF4",
      icon: "check",
      filterValue: "active",
    },
    {
      label: "Degraded",
      value: routers.filter((r) => getRouterStatus(r) === "degraded").length,
      color: "#D97706",
      bg: "#FEF3C7",
      icon: "alert",
      filterValue: "degraded",
    },
    {
      label: "Inactive / Down",
      value: routers.filter((r) => {
        const s = getRouterStatus(r);
        return s === "inactive" || s === "decommissioned" || s === "down";
      }).length,
      color: "#DC2626",
      bg: "#FEF2F2",
      icon: "alert",
      filterValue: "inactive",
    },
  ];

  const networkColumns = [
    {
      header: "Device Name",
      tip: "Appliance identifier & host details",
      render: (_, router) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              background: "#F3E8FF",
              border: "1px solid #E9D5FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="network" size={15} color="#6B21A8" />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
              {router.name}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "#64748B",
                fontFamily: "monospace",
                whiteSpace: "nowrap",
              }}
            >
              {router.id}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: "Hostname / IP",
      tip: "Network address and FQDN",
      cellStyle: { fontFamily: "monospace", fontSize: 12, color: "#334155" },
      render: (_, router) => router.hostname,
    },
    {
      header: "Status",
      tip: "Real-time telemetry / reachability status",
      render: (_, router) => <StatusBadge status={getRouterStatus(router)} />,
    },
    {
      header: "Type",
      tip: "Appliance taxonomy",
      render: (_, router) => {
        const cat = getAssetCategory(router);
        const isSwitch = cat === "switch";
        const isFirewall = cat === "firewall";
        const isLoadBalancer = cat === "load_balancer";
        const iconName = isSwitch
          ? "layers"
          : isFirewall
          ? "shield"
          : isLoadBalancer
          ? "cpu"
          : "network";
        const badgeBg = isSwitch
          ? "#E0F2FE"
          : isFirewall
          ? "#FEE2E2"
          : isLoadBalancer
          ? "#FEF3C7"
          : "#F3E8FF";
        const badgeColor = isSwitch
          ? "#0369A1"
          : isFirewall
          ? "#991B1B"
          : isLoadBalancer
          ? "#92400E"
          : "#6B21A8";
        const badgeBorder = isSwitch
          ? "1px solid #BAE6FD"
          : isFirewall
          ? "1px solid #FECACA"
          : isLoadBalancer
          ? "1px solid #FDE68A"
          : "1px solid #E9D5FF";
        return (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.03em",
              padding: "3px 8px",
              borderRadius: 6,
              background: badgeBg,
              color: badgeColor,
              border: badgeBorder,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              textTransform: "uppercase",
            }}
          >
            <Icon name={iconName} size={10} color={badgeColor} />
            {formatCategoryLabel(cat)}
          </span>
        );
      },
    },
    {
      header: "Vendor & Model",
      tip: "Hardware make and model",
      style: { maxWidth: 160 },
      cellStyle: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      render: (_, router) => {
        const { vendor, model } = getAssetVendorModel(router);
        const fullStr = model
          .toLowerCase()
          .startsWith(vendor.toLowerCase())
          ? model
          : `${vendor} ${model}`;
        return (
          <Tooltip text={fullStr}>
            <span
              style={{
                background: "#F1F5F9",
                padding: "3px 8px",
                borderRadius: 6,
                border: "1px solid #E2E8F0",
                fontWeight: 600,
                fontSize: 11,
                color: "#334155",
                display: "inline-block",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fullStr}
            </span>
          </Tooltip>
        );
      },
    },
    {
      header: "Latency (Ping)",
      tip: "ICMP echo round-trip response time",
      render: (_, router) => {
        const lat = getRouterLatency(router);
        return lat !== null ? (
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: lat > 50 ? "#D97706" : "#16A34A",
            }}
          >
            {lat.toFixed(1)} ms
          </span>
        ) : (
          "—"
        );
      },
    },
    {
      header: "Health Score",
      tip: "Health score returned by the routers API",
      render: (_, router) => {
        const score = getRouterHealthScore(router);
        if (score === null) {
          return <span style={{ color: "#94A3B8" }}>—</span>;
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 60,
                height: 6,
                background: "#E2E8F0",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.max(0, Math.min(100, score))}%`,
                  height: "100%",
                  background:
                    score >= 80
                      ? "#10B981"
                      : score >= 50
                      ? "#F59E0B"
                      : "#EF4444",
                  borderRadius: 3,
                }}
              />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>
              {score === 0 ? "0%" : `${score.toFixed(1)}%`}
            </span>
          </div>
        );
      },
    },
    {
      header: "Management",
      tip: "SNMP / SSH / Agent connection type",
      render: (_, router) => (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            padding: "3px 8px",
            borderRadius: 6,
            background:
              router.management_type === "agent" ? "#E0F2FE" : "#F3E8FF",
            color:
              router.management_type === "agent" ? "#0369A1" : "#6B21A8",
            border:
              router.management_type === "agent"
                ? "1px solid #BAE6FD"
                : "1px solid #E9D5FF",
            textTransform: "uppercase",
          }}
        >
          {formatManagementLabel(router)}
        </span>
      ),
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Top Header Container matching CMDBView & DiscoveryView */}
      <div
        style={{
          padding: "18px 24px 14px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Network Infrastructure
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B7280" }}>
            Live SNMP & ICMP telemetry monitoring for agentless routers,
            switches, firewalls, and WAN uplinks
          </p>
        </div>

        {/* Operational Summary Cards - Interactive Filters */}
        <KpiGrid columns={5}>
          {statCards.map((card) => (
            <KpiCard
              key={card.label}
              icon={card.icon}
              label={card.label}
              value={loading ? <Skeleton width={40} height={20} /> : card.value}
              color={card.color}
              bg={card.bg}
              active={statusFilter === card.filterValue}
              onClick={() => {
                setStatusFilter(card.filterValue);
                setPage(1);
              }}
            />
          ))}
        </KpiGrid>

        {/* Navigation Tabs */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 14,
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          {[
            { id: "appliances", label: "Network Appliances", icon: "server" },
            {
              id: "topology",
              label: "Network Topology & Dependencies",
              icon: "network",
            },
          ].map((tab) => {
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  setPage(1);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  fontSize: 13,
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? "#2563EB" : "#6B7280",
                  border: "none",
                  borderBottom: isSelected
                    ? "2px solid #2563EB"
                    : "2px solid transparent",
                  background: "transparent",
                  cursor: "pointer",
                  marginBottom: -1,
                }}
              >
                <Icon name={tab.icon} size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Filter and Search Bar */}
        <div style={{ display: "flex", gap: 10 }}>
          <SearchInput
            placeholder="Search network appliances by name, hostname, vendor, or model..."
            value={searchQuery}
            onChange={(val) => {
              setSearchQuery(val);
              setPage(1);
            }}
            style={{ flex: 1 }}
          />
          <FilterSelect
            value={statusFilter}
            onChange={(val) => {
              setStatusFilter(val);
              setPage(1);
            }}
            placeholder="All Active / Monitored Statuses"
            options={[
              { value: "active", label: "Active / Healthy" },
              { value: "degraded", label: "Degraded" },
              { value: "inactive", label: "Inactive / Down" },
              { value: "decommissioned", label: "Decommissioned" },
            ]}
          />
        </div>
      </div>

      {/* View Content Tabs */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        {activeTab === "topology" && (
          <div>
            {topologyError ? (
              <ErrorState message={topologyError} onRetry={fetchTopology} />
            ) : topologyLoading ? (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} height={80} style={{ borderRadius: 8 }} />
                ))}
              </div>
            ) : (
              <TopologyView
                topology={topology}
                servers={servers}
                initialDomain="infrastructure"
                healthField="health_score"
                onSelectServer={setSelectedRouter}
              />
            )}
          </div>
        )}

        {activeTab === "appliances" && (
          <DataTable
            columns={networkColumns}
            data={filteredRouters}
            loading={loading}
            error={error}
            onRetry={fetchAllData}
            emptyTitle={
              routers.length === 0
                ? "No Network Routers Monitored"
                : "No matching network assets"
            }
            emptySubtitle={
              routers.length === 0
                ? "No agentless SNMP routers or network appliances have been discovered yet. Once an administrator configures a monitored subnet and runs a discovery sweep, core network devices will automatically appear here."
                : "No network appliances match your search query or status filter. Try clearing your search query."
            }
            emptyIcon={routers.length === 0 ? "network" : "search"}
            onRowClick={(router) => setSelectedRouter(router)}
            page={page}
            total={filteredRouters.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            itemLabel="appliances"
          />
        )}
      </div>

      {/* Selected Router Detail Modal */}
      {selectedRouter && (
        <ServerDetailModal
          server={selectedRouter}
          healthField="health_score"
          onClose={() => setSelectedRouter(null)}
        />
      )}
    </div>
  );
}
